import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import LeftBox from './components/LeftBox';
import RightBox from './components/RightBox';
import classNames from 'classnames';
import CenterTopBox from './components/CenterTopBox';
import CenterBottomBox from './components/CenterBottomBox';
import {
  apiV2ElectricMapConfigOverviewDetailPost,
  apiV2ElectricMapDataOverviewCardDayPost,
  apiV2ElectricMapDataOverviewCardMonthPost,
  apiV2ElectricMapDataOverviewCardYearPost,
  apiV2ElectricMapDataScalePost,
  V2ElectricMapDataOverviewCardDayPostResponse,
  V2ElectricMapDataOverviewCardMonthPostResponse,
  V2ElectricMapDataOverviewCardYearPostResponse,
  V2ElectricMapDataScalePostResponse,
  V2ElectricMapConfigOverviewDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { ShareDataWrapperContext } from '../..';
import UnityMap from './components/UnityMap';
import dayjs, { Dayjs } from 'dayjs';
import AiAssistant from '../AiAssistant';
import MapView from './components/MapView';
import type { WgqMapMode } from '../..';
import { IndexTimeActive } from './components/LeftBox/components/SwitchDate';

export { IndexTimeActive };

interface ShareDataProps {
  scaleData?: V2ElectricMapDataScalePostResponse;
  dayData?: V2ElectricMapDataOverviewCardDayPostResponse;
  monthData?: V2ElectricMapDataOverviewCardMonthPostResponse;
  yearData?: V2ElectricMapDataOverviewCardYearPostResponse;
  configDetail?: V2ElectricMapConfigOverviewDetailPostResponse;
  timeResolution?: IndexTimeActive;
  dateValue?: Dayjs;
  setValue?: React.Dispatch<React.SetStateAction<Dayjs | undefined>>;
  setActiveKey?: React.Dispatch<React.SetStateAction<IndexTimeActive>>;
  rightExpanded?: boolean;
  setRightExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ShareDataUnityPowerMapContext = createContext<ShareDataProps>({});
export const ShareDataPowerMapContext = ShareDataUnityPowerMapContext;

const AI_ASSISTANT_SIZE = { width: 96, height: 128 };

const UnityPowerMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isFullScreen, isStandardSize, loadMap, scale: pageScale } = useContext(ShareDataWrapperContext);
  const [activeKey, setActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [value, setValue] = useState<Dayjs>();
  const [rightExpanded, setRightExpanded] = useState(false);

  // AI助手定位
  const aiAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const date = dayjs().date() === 1 ? dayjs().subtract(1, 'day') : dayjs();
    setValue(date);
  }, []);

  const { data: configDetail } = useRequest(async () => {
    return await apiV2ElectricMapConfigOverviewDetailPost({});
  });

  const { data: scaleData } = useRequest(() =>
    apiV2ElectricMapDataScalePost({}).then(res => {
      return res;
    })
  );

  const { data: dayData } = useRequest(() =>
    apiV2ElectricMapDataOverviewCardDayPost({}).then(res => {
      return res;
    })
  );

  const { data: monthData } = useRequest(() =>
    apiV2ElectricMapDataOverviewCardMonthPost({}).then(res => {
      return res;
    })
  );

  const { data: yearData } = useRequest(() =>
    apiV2ElectricMapDataOverviewCardYearPost({}).then(res => {
      return res;
    })
  );

  const renderCenterStandardScreen = useMemo(() => {
    return (
      <div className={classNames(styles.content_layer, styles.content_layer_no_map)}>
        <div className={classNames(styles.center_left_box, styles.noMapWidth, styles.noMapColumn)}>
          <CenterTopBox isNoMapLayout />
          <LeftBox configDetail={configDetail} />
        </div>
        <div className={classNames(styles.center_right_box, styles.noMapWidth, styles.noMapColumn)}>
          <CenterBottomBox configDetail={configDetail} />
          <RightBox />
        </div>
      </div>
    );
  }, [configDetail]);

  const MapComponent = mapMode === 'unityMap' ? UnityMap : MapView;

  return (
    <ShareDataUnityPowerMapContext.Provider
      value={{
        scaleData,
        dayData,
        monthData,
        yearData,
        configDetail,
        timeResolution: activeKey,
        dateValue: value,
        setActiveKey,
        setValue,
        rightExpanded,
        setRightExpanded,
      }}
    >
      {isStandardSize ? (
        loadMap ? (
          <div
            className={`${styles.inner_center} ${styles.inner_center_standard}`}
            style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
          >
            <MapComponent className={`${isFullScreen ? styles.fullMap : styles.map}`}>
              <CenterTopBox className={styles.centerBox} />
              <CenterBottomBox className={styles.centerBottomBox} configDetail={configDetail} />
            </MapComponent>
          </div>
        ) : (
          <div
            className={classNames(styles.inner_center, styles.inner_center_standard, styles.inner_center_no_map)}
            style={{ background: '#111D2C' }}
          >
            {renderCenterStandardScreen}
          </div>
        )
      ) : (
        <div
          className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}
          style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
        >
          {loadMap && <MapComponent className={styles.map}></MapComponent>}
          {loadMap && (
            <div className={styles.center_center_box}>
              <CenterTopBox className={styles.centerBox} />
            </div>
          )}
          <div
            className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}
            style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
          >
            <div
              className={classNames(
                styles.center_left_box,
                !rightExpanded && !isStandardSize && loadMap ? styles.center_left_box_collapsed : '',
                !loadMap ? styles.noMapWidth : '',
                !loadMap ? styles.noMapColumn : ''
              )}
            >
              {!loadMap && <CenterTopBox isNoMapLayout />}
              <LeftBox configDetail={configDetail} />
            </div>
            <div
              className={classNames(
                styles.center_right_box,
                !loadMap ? styles.noMapWidth : '',
                !isStandardSize && loadMap ? styles.center_right_box_collapsed : '',
                !loadMap ? styles.noMapColumn : ''
              )}
            >
              <RightBox />
            </div>
          </div>
          {/* AI助手锚点（用于计算初始位置，不可见但占位） */}
          {!isStandardSize && loadMap && (
            <div
              ref={aiAnchorRef}
              className={styles.aiRobotWrapper}
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
              aria-hidden
            />
          )}
          {/* AI助手（可拖拽） */}
          <AiAssistant
            anchorRef={aiAnchorRef}
            placement="anchor"
            baseSize={AI_ASSISTANT_SIZE}
            pageScale={pageScale}
            enabled={!isStandardSize && loadMap}
          />
        </div>
      )}
    </ShareDataUnityPowerMapContext.Provider>
  );
};

export default UnityPowerMap;
